import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { parseCallbackParams, getPKCEValues, exchangeCodeForSession, initiateLogin, isLocalDevHost } from '../lib/authentik';
import { supabase } from '../lib/supabase';
import LoadingSpinner from './LoadingSpinner';
import { AlertCircle, CheckCircle } from 'lucide-react';
import './AuthCallback.css';

/**
 * OAuth Callback Handler Component
 *
 * This component handles the redirect back from Authentik after login.
 * It exchanges the authorization code for a Supabase session.
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [error, setError] = useState(null);
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double-processing in React strict mode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const handleCallback = async () => {
      try {
        // Parse callback parameters from URL
        const { code, state, error: oauthError, errorDescription } = parseCallbackParams();

        console.log('OAuth callback received:', { code: !!code, state: !!state, error: oauthError });

        // Check for OAuth errors
        if (oauthError) {
          throw new Error(errorDescription || oauthError);
        }

        if (!code) {
          throw new Error('No authorization code received');
        }

        // Get stored PKCE values
        const { codeVerifier, state: storedState } = getPKCEValues();

        console.log('PKCE values:', { hasVerifier: !!codeVerifier, hasStoredState: !!storedState, statesMatch: state === storedState });

        // Validate state parameter (CSRF protection)
        // Skip state validation if storedState is missing (may have been cleared by strict mode)
        if (storedState && state !== storedState) {
          // State mismatch - likely caused by double-click or multiple tabs
          // Auto-retry instead of showing error (since "Try Again" always works)
          console.warn('[Auth] State mismatch detected (likely double-click), restarting login flow...');
          console.log('[Auth] Expected state:', storedState, 'Received state:', state);

          // Clear any remaining PKCE values
          sessionStorage.removeItem('pkce_code_verifier');
          sessionStorage.removeItem('oauth_state');

          // Auto-restart the login flow
          await initiateLogin();
          return; // initiateLogin will redirect
        }

        if (!codeVerifier) {
          // If no code verifier, we need to restart the flow
          console.log('No code verifier found, restarting login flow...');
          navigate('/', { replace: true });
          return;
        }

        setStatus('processing');

        // Exchange code for Authentik tokens and get user info
        const result = await exchangeCodeForSession(code, codeVerifier);

        if (!result.success) {
          throw new Error(result.message || 'Failed to authenticate');
        }

        const { user: authentikUser } = result;
        console.log('Authentik user:', authentikUser.email);

        // Try to sign in with existing Supabase user
        // First, check if user exists by trying a password sign-in
        // (This will fail but tells us if user exists)

        // For now, we'll use the API endpoint to handle Supabase user creation
        // But since we can't call it directly from browser, we need a workaround

        // Call our API endpoint to create/update the Supabase user
        const apiResponse = await fetch('/api/auth/authentik-callback', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            authentikUser: authentikUser,
            tokens: result.tokens,
          }),
        });

        if (apiResponse.ok) {
          const apiResult = await apiResponse.json();
          console.log('API result:', apiResult);

          // If we got session tokens, set them
          if (apiResult.session) {
            await supabase.auth.setSession({
              access_token: apiResult.session.access_token,
              refresh_token: apiResult.session.refresh_token,
            });
          }
        } else {
          // Outside local dev a failed callback must surface as an error. Falling
          // through would leave the user "signed in" with no Supabase session,
          // so RLS returns empty tables and the app looks like it has no data.
          if (!isLocalDevHost()) {
            let detail = '';
            try {
              detail = (await apiResponse.json())?.message || '';
            } catch (e) {
              // Non-JSON error body - the status code is enough
            }
            throw new Error(
              `Sign-in service error (${apiResponse.status})${detail ? `: ${detail}` : ''}. Please try again.`
            );
          }

          // API not available (local dev without vercel dev)
          // For local development, store Authentik user info and use service role via anon key
          console.warn('API endpoint not available - using local dev mode with Authentik user');

          // Store Authentik user info for local dev session
          localStorage.setItem('authentik_user', JSON.stringify({
            email: authentikUser.email,
            name: authentikUser.name || authentikUser.preferred_username,
            sub: authentikUser.sub,
            authenticated_at: new Date().toISOString(),
          }));
          localStorage.setItem('authentik_tokens', JSON.stringify(result.tokens));

          // Clear the SSO redirect guard since we're now authenticated
          sessionStorage.removeItem('sso_redirect_initiated');

          console.log('Local dev mode: Authentik user stored, proceeding without Supabase session');
          // Skip the session check below - we're authenticated via Authentik
          navigate('/', { replace: true });
          return;
        }

        // Clear the SSO redirect guard since we're now authenticated
        sessionStorage.removeItem('sso_redirect_initiated');

        // Verify we have a session now
        const { data: { session } } = await supabase.auth.getSession();

        if (!session) {
          throw new Error('Failed to establish Supabase session');
        }

        // Redirect immediately to dashboard
        navigate('/', { replace: true });

      } catch (err) {
        console.error('Auth callback error:', err);
        setError(err.message);
        setStatus('error');
      }
    };

    handleCallback();
  }, [navigate]);

  return (
    <div className="auth-callback-container">
      <div className="auth-callback-card">
        {status === 'processing' && (
          <>
            <LoadingSpinner size="large" />
            <h2>Signing you in...</h2>
            <p>Please wait while we complete your authentication.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="auth-callback-icon success">
              <CheckCircle size={48} />
            </div>
            <h2>Welcome!</h2>
            <p>Authentication successful. Redirecting...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="auth-callback-icon error">
              <AlertCircle size={48} />
            </div>
            <h2>Authentication Failed</h2>
            <p className="error-message">{error}</p>
            <button
              onClick={() => navigate('/', { replace: true })}
              className="retry-button"
            >
              Try Again
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
